import { useState, useCallback } from 'react';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';

interface CompetitionAnswerQuestion {
  questionKey: string;
  answerKey: string;
}

interface CompetitionAnswerRequest {
  questions: CompetitionAnswerQuestion[];
}

interface CompetitionAnswerResponse {
  isCorrect: boolean;
}

interface UseCompetitionAnswerResult {
  submitEntry: (competitionId: string, answers: Record<string, string>) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
  isCorrect: boolean;
}

export const useCompetitionAnswer = (): UseCompetitionAnswerResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const submitEntry = useCallback(async (competitionId: string, answers: Record<string, string>) => {
    setIsLoading(true);
    setError(null);
    setIsSuccess(false);

    const request: CompetitionAnswerRequest = {
      questions: Object.entries(answers).map(([questionKey, answerKey]) => ({
        questionKey,
        answerKey,
      })),
    };

    try {
      const response = await apiClient.post<CompetitionAnswerResponse>(ApiType.apimNafNoApi, {
        url: ApiPaths[ApiType.apimNafNoApi].competitions.entries(competitionId),
        data: request,
      });
      setIsCorrect(response.data.isCorrect);
      setIsSuccess(true);
    } catch (e: unknown) {
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    submitEntry,
    isLoading,
    error,
    isSuccess,
    isCorrect,
  };
};
